// ErrorBoundary (Parte 14.2/14.5): erro de renderização com correlationId visível e ação de repetir.
// Erros da API trazem o correlationId do servidor; erros locais recebem um id de cliente para o suporte cruzar logs.
import { GiyusApiError, GiyusNetworkError } from '@giyus/api-client';
import { Alert, AlertDescription, AlertTitle, Button } from '@giyus/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_VERSION } from '../lib/env';
import { correlationIdOf, defaultClientCorrelationId } from './correlation';

export interface ErrorFallbackProps {
  readonly error: unknown;
  readonly correlationId: string;
  readonly onRetry: () => void;
}

export function ErrorFallback({ error, correlationId, onRetry }: ErrorFallbackProps) {
  const { t } = useTranslation(['shell', 'common', 'errors']);
  const detail =
    error instanceof GiyusApiError
      ? t(`errors:codes.${error.errorCode}`, {
          defaultValue: t('errors:generic.unknown', { errorCode: error.errorCode }),
        })
      : error instanceof GiyusNetworkError
        ? t('errors:generic.network')
        : t('errors:generic.render');
  return (
    <div className="mx-auto max-w-2xl p-6" data-testid="error-boundary">
      <Alert variant="destructive">
        <AlertTitle>{t('shell:error.title')}</AlertTitle>
        <AlertDescription>
          <p>{t('shell:error.message')}</p>
          <p>{detail}</p>
          <p className="font-mono text-caption" data-testid="error-correlation-id">
            {t('common:state.correlationId', { id: correlationId })}
          </p>
          <p
            className="font-mono text-caption text-muted-foreground"
            data-testid="error-app-version"
          >
            {t('shell:footer.version', { version: APP_VERSION })}
          </p>
          <div className="pt-2">
            <Button size="sm" variant="outline" onClick={onRetry}>
              {t('common:actions.retry')}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly onError?: (error: unknown, info: ErrorInfo, correlationId: string) => void;
  readonly generateCorrelationId?: () => string;
}

interface ErrorBoundaryState {
  readonly error: unknown;
  readonly correlationId: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, correlationId: null };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const correlationId = correlationIdOf(
      error,
      this.props.generateCorrelationId ?? defaultClientCorrelationId,
    );
    this.setState({ correlationId });
    if (this.props.onError) {
      this.props.onError(error, info, correlationId);
    } else {
      console.error(`[giyus] render error (correlationId=${correlationId})`, error);
    }
  }

  private readonly retry = (): void => {
    this.setState({ error: null, correlationId: null });
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <ErrorFallback
          error={this.state.error}
          correlationId={this.state.correlationId ?? ''}
          onRetry={this.retry}
        />
      );
    }
    return this.props.children;
  }
}
